import {
  Building2,
  Shield,
  Users,
  Heart,
  TrendingUp,
  FileText,
  Database,
  Award,
  UserCheck,
  DollarSign,
  Wallet,
  Activity,
  CheckCircle,
  Settings,
} from "lucide-react";

export default function PlatformPage() {
  const modules = [
    {
      icon: <Building2 className="h-6 w-6" />,
      title: "Hostel Registration Engine",
      description: "Streamlined onboarding for new institutions",
    },
    {
      icon: <Shield className="h-6 w-6" />,
      title: "Trustee Credentialing System",
      description: "Verification and credentialing of trustees",
    },
    {
      icon: <Users className="h-6 w-6" />,
      title: "Student Onboarding & Verification",
      description: "Digital student enrollment and verification",
    },
    {
      icon: <UserCheck className="h-6 w-6" />,
      title: "Parent Access Portal",
      description: "Real-time access for parents to track student welfare",
    },
    {
      icon: <DollarSign className="h-6 w-6" />,
      title: "Donor & CSR Dashboard",
      description: "Transparent impact tracking for donors",
    },
    {
      icon: <Wallet className="h-6 w-6" />,
      title: "Vendor & Contractor Directory",
      description: "Verified vendor and service provider registry",
    },
    {
      icon: <Heart className="h-6 w-6" />,
      title: "Health/Nutrition/Sports Expert Directory",
      description: "Empanelled wellness professionals",
    },
    {
      icon: <Database className="h-6 w-6" />,
      title: "MIS & Data Collection Layer",
      description: "Comprehensive data management system",
    },
    {
      icon: <FileText className="h-6 w-6" />,
      title: "Governance & Compliance Panel",
      description: "Real-time governance monitoring",
    },
    {
      icon: <TrendingUp className="h-6 w-6" />,
      title: "Impact Measurement Panel (SROI)",
      description: "Social Return on Investment tracking",
    },
    {
      icon: <CheckCircle className="h-6 w-6" />,
      title: "Credit Minting (SICM)",
      description: "Social Impact Credit generation",
    },
    {
      icon: <CheckCircle className="h-6 w-6" />,
      title: "SIRC Compliance Engine",
      description: "Social Impact Rating & Compliance",
    },
    {
      icon: <Award className="h-6 w-6" />,
      title: "HDT Recognition Console",
      description: "Hostel Development Token allocation",
    },
  ];

  const dataRegistry = [
    { icon: <Building2 className="h-8 w-8" />, label: "Hostels" },
    { icon: <Shield className="h-8 w-8" />, label: "Trustees" },
    { icon: <Settings className="h-8 w-8" />, label: "Operators" },
    { icon: <Users className="h-8 w-8" />, label: "Students" },
    { icon: <UserCheck className="h-8 w-8" />, label: "Parents" },
    { icon: <DollarSign className="h-8 w-8" />, label: "Donors" },
    { icon: <Wallet className="h-8 w-8" />, label: "CSR Bodies" },
    { icon: <Heart className="h-8 w-8" />, label: "Volunteers" },
    { icon: <Award className="h-8 w-8" />, label: "Vendors" },
    { icon: <Activity className="h-8 w-8" />, label: "Experts" },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative flex min-h-[40vh] items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1736751035793-353baaa416cf?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGFuYWx5dGljcyUyMGdyYXBocyUyMGNoYXJ0c3xlbnwxfHx8fDE3NjU1NDA0ODR8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
            alt="Digital platform technology"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#7A1E1E]/80 to-[#8B2E2E]/70" />
        </div>

        <div className="relative z-10 mx-auto mb-10 mt-10 max-w-[1400px] px-4 text-white sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="mb-6 text-5xl lg:text-6xl">
              The JBF Digital Governance Platform
            </h1>
            <p className="mb-4 text-2xl leading-relaxed text-gray-100">
              A Unified National System for Jain Hostels
            </p>
            <p className="text-lg text-gray-200">
              Built on the MAA SaaS platform, JBF provides a comprehensive
              digital ecosystem for governance, transparency, and impact
              measurement.
            </p>
          </div>
        </div>
      </section>

      {/* Modules Available Section */}
      <section className="bg-[#FFF5E6] py-16 lg:py-24">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl text-[#7A1E1E] sm:text-4xl">
              Modules Available for JBF Institutions
            </h2>
            <div className="mx-auto mb-6 h-1 w-24 bg-[#D4A017]" />
            <p className="mx-auto max-w-3xl text-lg text-gray-700">
              Comprehensive suite of integrated modules covering every aspect of
              hostel management.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {modules.map(module => (
              <div
                key={module.title}
                className="group rounded-xl border-l-4 border-[#D4A017] bg-white p-6 shadow-md transition-all hover:bg-[#7A1E1E] hover:shadow-xl"
              >
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[#7A1E1E] to-[#8B2E2E] text-white transition-all group-hover:bg-[#D4A017]">
                    {module.icon}
                  </div>
                  <div>
                    <h3 className="mb-2 text-base text-[#7A1E1E] transition-colors group-hover:text-white">
                      {module.title}
                    </h3>
                    <p className="text-sm text-gray-700 transition-colors group-hover:text-gray-200">
                      {module.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Central Database Architecture */}
      <section className="bg-white py-16 lg:py-24">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl text-[#7A1E1E] sm:text-4xl">
              Central Database Architecture
            </h2>
            <div className="mx-auto mb-6 h-1 w-24 bg-[#D4A017]" />
            <p className="mx-auto max-w-3xl text-lg text-gray-700">
              JBF maintains a National Data Registry for comprehensive
              stakeholder management through RBAC-based CRM (Role-Based Access
              Control).
            </p>
          </div>

          <div className="mb-12 grid grid-cols-2 gap-6 md:grid-cols-3 lg:grid-cols-5">
            {dataRegistry.map(item => (
              <div
                key={item.label}
                className="transform rounded-xl bg-gradient-to-br from-[#FFF9F0] to-[#FFF5E6] p-6 text-center shadow-md transition-all hover:-translate-y-2 hover:shadow-xl"
              >
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-[#7A1E1E] to-[#8B2E2E] text-white">
                  {item.icon}
                </div>
                <p className="text-gray-800">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
